import Big from 'big.js';
import classNames from 'classnames';
import { get, isNumber } from 'lodash';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import { BigRoundMode } from '@/modules/common/constants/big-round-mode.ts';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { useRegisterQuery } from '@/modules/common/queries/registers.ts';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import { fileToBase64, normalizeFileName } from '@/modules/common/utils/file.ts';
import { DividendOperationInfoModal } from '@/modules/issuer-dashboard/components/distribution-dashboard/DividendOperationInfoModal.tsx';
import { IssuerConfirmPaymentModal } from '@/modules/issuer-dashboard/components/modals/IssuerConfirmPaymentModal.tsx';
import { IssuerConfirmTransactionCompletedModal } from '@/modules/issuer-dashboard/components/modals/IssuerConfirmTransactionCompletedModal.tsx';
import { IssuerConfirmTransactionModal } from '@/modules/issuer-dashboard/components/modals/IssuerConfirmTransactionModal.tsx';
import { useUpdateDocumentsMutation } from '@/modules/issuer-dashboard/queries/documents.ts';
import { usePaymentSetRejectedMutation } from '@/modules/issuer-dashboard/queries/payments.tsx';
import {
  useIssuerTransactionByIdQuery,
  useTransactionSetRejectedMutation,
} from '@/modules/issuer-dashboard/queries/transactions.ts';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  Badge,
  BaseModalProps,
  Button,
  DeclineModal,
  DisclosureListItem,
  Dropzone,
  ModalContentWrapper,
  Spinner,
  TooltipNew,
} from 'ui';
import { EditLightGrayIcon, TrashIcon } from 'ui/icons';
import {
  fixNameEnum,
  formatBytesSize,
  goodFormatNumber,
  goodFormatUSDCurrency,
  goodFormatUSDCurrencyLowPrecision,
} from 'ui/utils';
import {
  OperationDocumentResponse,
  PaymentSetRejectedStatusRequest,
  RoundingType,
  TransactionSetRejectStatusRequest,
  TransactionStatus,
  TransactionType,
  TransactionWithStorageItemUrl,
} from 'api';

export const InfoItem = ({
  title,
  children,
  align = 'center',
}: {
  title: string;
  children: ReactNode;
  align?: 'center' | 'start';
}) => {
  return (
    <div
      className={`w-full flex text-sm gap-4 ${align === 'start' ? 'items-start' : 'items-center'}`}
    >
      <div className="text-gray-500 min-w-[38%]">{title}</div>
      {children}
    </div>
  );
};

interface IssuerTransactionsOperationInfoModalProps extends BaseModalProps {
  id: number;
  title?: string;
  registerId: string;
  registerCountry: string;
  isDeleted?: boolean;
}

export const IssuerTransactionsOperationInfoModal = ({
  id,
  title = 'Transaction info',
  open,
  onClose,
  registerId,
  registerCountry,
  isDeleted = false,
}: IssuerTransactionsOperationInfoModalProps) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const [openConfirmPayment, setOpenConfirmPayment] = useState(false);
  const [openDividend, setOpenDividend] = useState(false);
  const [openRejectPayment, setOpenRejectPayment] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openConfirmCompleted, setOpenConfirmCompleted] = useState(false);
  const [openReject, setOpenReject] = useState(false);
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const { data, isLoading: isOperationLoading } = useIssuerTransactionByIdQuery(
    {
      id,
      is_deleted: isDeleted,
    },
    !open || !id,
  );
  const { mutateAsync: rejectTransaction } = useTransactionSetRejectedMutation();
  const { mutateAsync: rejectPayment } = usePaymentSetRejectedMutation();

  const operationData = get(data, 'transaction') as TransactionWithStorageItemUrl;

  const completionDate = get(operationData, 'confirmation_date', get(operationData, 'issue_date'));

  const getTypeTransaction = useCallback(
    (type?: TransactionType) => {
      if (type === TransactionType.Subscription)
        return getRegisterTypeStrings('Application', { capitalize: true });
      if (type === TransactionType.Drp) return 'Dividend reinvestment';
      return fixNameEnum(type);
    },
    [getRegisterTypeStrings],
  );
  const register_id = Number(params.get(REGISTER_ID_SEARCH_PARAM_KEY));
  const { data: registerData } = useRegisterQuery(register_id, !register_id);

  const { rounding_type, allows_fractional_shares = true } = useMemo(() => {
    const { rounding_type, allows_fractional_shares } = registerData?.data ?? {};
    return { rounding_type, allows_fractional_shares };
  }, [registerData]);

  const quantity = useMemo(() => {
    if (!(!operationData?.quantity && operationData?.total && operationData?.price_per_share))
      return null;

    let num = new Big(operationData.total).div(operationData.price_per_share);

    if (allows_fractional_shares) {
      return Number(num.round(2, BigRoundMode.roundHalfUp).valueOf());
    } else {
      if (num.lt(0.5)) {
        return 0;
      }

      if (num.lt(1)) {
        return 1;
      }

      num =
        rounding_type === RoundingType.Up
          ? num.round(0, BigRoundMode.roundUp)
          : num.round(0, BigRoundMode.roundDown);
    }

    return Number(num.valueOf());
  }, [
    allows_fractional_shares,
    operationData?.price_per_share,
    operationData?.quantity,
    operationData?.total,
    rounding_type,
  ]);

  return (
    <ModalContentWrapper
      variant="right"
      title={
        <div className="flex gap-4 items-center">
          <span>{title}</span>
          {isDeleted && (
            <Badge className="bg-red-400 text-white text-xs font-[500] font-[Inter]">
              Archived
            </Badge>
          )}
        </div>
      }
      open={open}
      onClose={onClose}
    >
      {isOperationLoading && (
        <div className="w-full h-full flex justify-center items-center">
          <Spinner size="lg" />
        </div>
      )}
      {!isOperationLoading && operationData && (
        <>
          <div className="flex flex-col justify-between gap-4 h-full">
            <div className="space-y-3">
              <InfoItem title="ID">
                <div>#{operationData.id}</div>
              </InfoItem>
              {operationData.dividend_event_id && (
                <InfoItem
                  title={`${getRegisterTypeStrings('dividend', { capitalize: true })} event ID`}
                >
                  <div
                    className="text-blue-700 cursor-pointer hover:underline"
                    onClick={() => setOpenDividend(true)}
                  >
                    #{operationData.dividend_event_id}
                  </div>
                </InfoItem>
              )}
              <InfoItem title="Status">
                <ActivityStatusBadge status={operationData.status} />
              </InfoItem>
              <InfoItem title="Class">
                <Link
                  state={{ from: pathname + '?' + params.toString() }}
                  to={{
                    pathname: issuerDashboardPaths.dynamic.classById(
                      (operationData.register_class?.id || '0').toString(),
                    ),
                    search: params.toString(),
                  }}
                  className="text-blue-700 hover:underline"
                >
                  {operationData.register_class.class_name}
                </Link>
              </InfoItem>

              <InfoItem title="Type">
                <div>{getTypeTransaction(operationData.type)}</div>
              </InfoItem>
              <InfoItem title="Holder">
                <Link
                  state={{ from: pathname + '?' + params.toString() }}
                  to={{
                    pathname: issuerDashboardPaths.dynamic.holder(
                      (operationData.shareholder_entity?.id || '0').toString(),
                    ),
                    search: params.toString(),
                  }}
                  className="text-blue-700 hover:underline"
                >
                  {operationData.shareholder_entity?.entity_name}
                </Link>
              </InfoItem>

              <InfoItem title="Units/shares">
                {isNumber(quantity) ? (
                  <div>{quantity}</div>
                ) : (
                  <div>{goodFormatNumber(operationData.quantity)}</div>
                )}
              </InfoItem>
              <InfoItem title="Price">
                <div>{goodFormatUSDCurrency(operationData.price_per_share)}</div>
              </InfoItem>
              <InfoItem title="Total">
                {!operationData.quantity && !!operationData.total ? (
                  <div>
                    {goodFormatUSDCurrencyLowPrecision(+new Big(operationData.total)?.valueOf())}
                  </div>
                ) : (
                  <div>
                    {goodFormatUSDCurrencyLowPrecision(
                      +new Big(operationData.quantity)
                        ?.mul(operationData.price_per_share || 0)
                        ?.valueOf(),
                    )}
                  </div>
                )}
              </InfoItem>
              <InfoItem title="Requested date">
                <div>{dateToPrint(operationData.requested_at_date, DateVariant.Information)}</div>
              </InfoItem>
              {operationData?.status !== TransactionStatus.Completed &&
                !!operationData.issue_date && (
                  <InfoItem title="Issue date">
                    <div>{dateToPrint(operationData.issue_date, DateVariant.Information)}</div>
                  </InfoItem>
                )}
              <InfoItem title="Completion date">
                <div>
                  {operationData?.status === TransactionStatus.Completed
                    ? dateToPrint(completionDate, DateVariant.Information)
                    : '-'}
                </div>
              </InfoItem>
              <InfoItem title="Memo">
                <div>{operationData.memo || '-'}</div>
              </InfoItem>
              {operationData.type !== TransactionType.Drp && !isDeleted && (
                <TransactionsDocuments
                  transactionId={id}
                  status={operationData?.status}
                  document_storage_item_urls={operationData?.document_storage_item_urls}
                />
              )}
            </div>
            {!isDeleted && (
              <div className="flex flex-col gap-4 w-full">
                {operationData?.status === TransactionStatus.Pending && (
                  <>
                    <Button
                      className="min-w-[90px] flex gap-4 items-center"
                      onClick={() => {
                        setOpenConfirm(true);
                      }}
                    >
                      <img alt="Edit" src={EditLightGrayIcon} className="w-5 h-5 min-w-5 min-h-5" />{' '}
                      Open confirmation window
                    </Button>

                    <Button
                      variant="secondary"
                      className="min-w-[90px]"
                      onClick={() => {
                        setOpenReject(true);
                      }}
                    >
                      Decline
                    </Button>
                  </>
                )}
                {operationData?.status === TransactionStatus.DividendPending && (
                  <>
                    <Button
                      className="min-w-[90px] flex gap-4 items-center"
                      onClick={() => {
                        setOpenConfirmCompleted(true);
                      }}
                    >
                      <img alt="Edit" src={EditLightGrayIcon} className="w-5 h-5 min-w-5 min-h-5" />{' '}
                      Confirm
                    </Button>

                    <Button
                      variant="secondary"
                      className="min-w-[90px]"
                      onClick={() => {
                        setOpenReject(true);
                      }}
                    >
                      Decline
                    </Button>
                  </>
                )}
                {operationData.status === TransactionStatus.AwaitingPayment &&
                  operationData?.payment_id && (
                    <>
                      <Button
                        className="min-w-[90px] flex gap-4 items-center"
                        onClick={() => {
                          setOpenConfirmPayment(true);
                        }}
                      >
                        <img
                          alt="Edit"
                          src={EditLightGrayIcon}
                          className="w-5 h-5 min-w-5 min-h-5"
                        />{' '}
                        Open confirmation window
                      </Button>

                      <Button
                        variant="secondary"
                        className="min-w-[90px]"
                        onClick={() => {
                          setOpenRejectPayment(true);
                        }}
                      >
                        Decline
                      </Button>
                    </>
                  )}
              </div>
            )}
          </div>
          {!!operationData?.payment_id && isNumber(operationData?.amount) && (
            <IssuerConfirmPaymentModal
              open={openConfirmPayment}
              onClose={() => setOpenConfirmPayment(false)}
              payment_id={operationData?.payment_id}
              price_per_share={operationData?.price_per_share}
              amount={operationData?.amount}
              isTransactionCompleted={operationData?.status === TransactionStatus.Completed}
            />
          )}

          {!!operationData?.payment_id && (
            <DeclineModal
              onClose={() => setOpenRejectPayment(false)}
              open={openRejectPayment}
              onConfirm={async (payload: PaymentSetRejectedStatusRequest) => {
                await rejectPayment({ ...payload, id: operationData?.payment_id || 0 });
              }}
            />
          )}
          <IssuerConfirmTransactionModal
            open={openConfirm}
            onClose={() => setOpenConfirm(false)}
            price_per_share={
              operationData?.price_per_share ||
              operationData?.register_class?.current_price_per_share
            }
            id={operationData?.id}
            status={operationData?.status}
            quantity={isNumber(quantity) ? quantity : operationData.quantity}
            register_class_id={operationData?.register_class_id}
            register_id={operationData?.register_id}
            register_class_name={operationData?.register_class?.class_name}
            type={operationData?.type}
            total={operationData?.total}
            issue_date={operationData?.issue_date}
          />
          {!!operationData && (
            <IssuerConfirmTransactionCompletedModal
              open={openConfirmCompleted}
              onClose={() => setOpenConfirmCompleted(false)}
              id={operationData.id}
              register_class_name={operationData?.register_class?.class_name}
              quantity={isNumber(quantity) ? quantity : operationData.quantity}
              price_per_share={
                operationData?.price_per_share ||
                operationData?.register_class?.current_price_per_share ||
                0
              }
              total={operationData?.total || 0}
              issue_date={operationData?.issue_date}
              memo={operationData?.memo}
            />
          )}
          <DeclineModal
            onClose={() => setOpenReject(false)}
            open={openReject}
            onConfirm={async (payload: TransactionSetRejectStatusRequest) => {
              await rejectTransaction({ ...payload, id: operationData?.id });
            }}
          />
          {!!operationData?.dividend_event_id && openDividend && (
            <DividendOperationInfoModal
              open={openDividend}
              id={operationData?.dividend_event_id}
              onClose={() => setOpenDividend(false)}
              registerId={registerId}
              registerCountry={registerCountry}
              isDeleted={isDeleted}
            />
          )}
        </>
      )}
    </ModalContentWrapper>
  );
};

interface TransactionsDocumentsProps {
  document_storage_item_urls?: OperationDocumentResponse[];
  transactionId?: number;
  transferId?: number;
  status?: TransactionStatus;
}

export const TransactionsDocuments = ({
  document_storage_item_urls,
  transactionId,
  transferId,
  status,
}: TransactionsDocumentsProps) => {
  const isCompleted = status === TransactionStatus.Completed;
  const [idsDelDoc, setIdsDelDoc] = useState<string[]>([]);
  const [isUpload, setIsUpload] = useState(false);
  const { mutateAsync } = useUpdateDocumentsMutation({
    onSuccess: payload => {
      if (payload.delete_document_ids) {
        setIdsDelDoc(prev => prev.filter(id => !payload.delete_document_ids?.includes(id)));
      }
      if (payload.upload_document) {
        setIsUpload(false);
      }
    },
  });
  const MAX_DOCUMENTS = 5;
  const isPossibleToUploadDocument = (document_storage_item_urls?.length || 0) < MAX_DOCUMENTS;

  const handleDeleteDocument = useCallback(
    (documentId: string) => async () => {
      setIdsDelDoc(prev => [...prev, documentId]);
      await mutateAsync({
        delete_document_ids: [documentId],
        transaction_id: transactionId,
        transfer_id: transferId,
      });
    },
    [transactionId, transferId, mutateAsync],
  );

  const handleAddDocuments = useCallback(
    async (files: File[]) => {
      setIsUpload(true);
      const base64FilesPromises = files.map(async (file: File) => {
        try {
          return await fileToBase64(file, true);
        } catch (e: any) {
          toast.error(`Error: ${e.message}`);
          return '';
        }
      });

      const base64Files = (await Promise.allSettled(base64FilesPromises)).reduce(
        (arr, p) => (p.status === 'fulfilled' ? [...arr, p.value] : arr),
        [] as string[],
      );

      await mutateAsync({
        upload_document: base64Files,
        transaction_id: transactionId,
        transfer_id: transferId,
      });
    },
    [transactionId, transferId, mutateAsync],
  );

  return (
    <>
      <div className="text-sm leading-[22px] font-[500] pt-3 border-t border-gray-200">
        Upload documents
      </div>
      <TooltipNew
        disabled={isPossibleToUploadDocument}
        text={`The maximum number of documents that can be attached is ${MAX_DOCUMENTS}.`}
      >
        <div className="[&>div>div]:min-h-[65px]">
          <Dropzone
            multiple
            maxFiles={MAX_DOCUMENTS - (document_storage_item_urls?.length || 0)}
            disabled={!isPossibleToUploadDocument}
            onFileAccepted={handleAddDocuments}
            onFileClear={() => 0}
            loading={isUpload}
            acceptFormat={['jpeg', 'pdf', 'png', 'tiff', 'xls', 'doc']} //
          />
        </div>
      </TooltipNew>
      {document_storage_item_urls?.map(doc => {
        const isDeletingDoc = idsDelDoc.includes(doc.document_id);
        return (
          <DisclosureListItem
            key={doc.document_id}
            leftContent={
              !doc.mimetype?.includes('application') ? (
                <img
                  src={doc.document_storage_item_url}
                  alt="Loading..."
                  className={classNames('max-h-[56px] max-w-[56px] aspect-auto')}
                />
              ) : doc.mimetype?.includes('pdf') ? (
                <iframe
                  src={doc.document_storage_item_url}
                  className="max-h-[56px] max-w-[56px] aspect-auto overflow-hidden"
                  allowFullScreen={true}
                ></iframe>
              ) : (
                <></>
              )
            }
            leftMainString={
              <div className="text-ellipsis overflow-hidden break-all whitespace-nowrap text-nowrap max-w-[250px]">
                {doc.document_storage_item_url ? (
                  <a
                    href={doc.document_storage_item_url}
                    className={classNames('text-blue-700 hover:underline')}
                    target="_blank"
                  >
                    {normalizeFileName(doc.file_name || 'Document')}
                  </a>
                ) : (
                  <>{normalizeFileName(doc.file_name || 'Document')}</>
                )}
              </div>
            }
            leftSubduedContentSet={[
              ...(doc.mimetype ? [doc.mimetype.split('/')?.[1]?.toUpperCase()] : []),
              ...(doc.size ? [`${formatBytesSize(doc.size)}`] : []),
            ]}
            classes={{
              leftSubduedContentSetItem:
                'text-ellipsis overflow-hidden break-all whitespace-nowrap text-nowrap max-w-[230px]',
            }}
            rightContent={
              <>
                {!isCompleted &&
                  (isDeletingDoc ? (
                    <Spinner size="sm" className="m-0 " />
                  ) : (
                    <button
                      disabled={isDeletingDoc}
                      className={classNames({
                        'cursor-pointer hover:opacity-70': !isDeletingDoc,
                      })}
                      onClick={handleDeleteDocument(doc.document_id)}
                    >
                      <img src={TrashIcon} alt="delete" className="min-w-5 min-h-5 w-5 h-5" />
                    </button>
                  ))}
              </>
            }
          />
        );
      })}
    </>
  );
};
